import { Plus } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { SkeletonRows } from '@/components/ui/skeleton'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { listSentence } from '@/lib/formatters'
import { checksForCard, type FillApplicationState, FillCard, hasFilesReading } from '@/models/fillApplication'
import { useOrgLossRunRequests } from '@/models/lossRunRequest'
import { AddClaimDialog } from './AddClaimDialog'
import { FillSectionCard } from './FillSectionCard'
import { FillTabSection } from './FillTabSection'
import { LossRunClaimsTable } from './LossRunClaimsTable'
import { LossRunDocumentsCard } from './LossRunDocumentsCard'
import { LossRunUploadButton } from './LossRunUploadButton'

export function LossRunsCard({ orgId, state }: { orgId: string; state: FillApplicationState }) {
  const { lossRunRequests, documents, isLoading } = useOrgLossRunRequests(orgId)
  const [adding, setAdding] = useState(false)
  const reading = hasFilesReading(state)
  const readingNames = state.inFlight.filter((row) => !row.stale).map((row) => row.fileName)

  const addButton = reading ? (
    <Tooltip>
      <TooltipTrigger asChild>
        <span tabIndex={0}>
          <Button type="button" variant="outline" disabled aria-disabled="true">
            <Plus /> Add loss run history
          </Button>
        </span>
      </TooltipTrigger>
      <TooltipContent>
        Wait until the files are read: a loss run among them could add the same claim.
      </TooltipContent>
    </Tooltip>
  ) : (
    <Button type="button" variant="outline" onClick={() => setAdding(true)}>
      <Plus /> Add loss run history
    </Button>
  )

  return (
    <FillTabSection
      card={FillCard.LOSS_RUNS}
      title="Loss runs"
      checks={checksForCard(state.checks, FillCard.LOSS_RUNS)}
      description={
        reading ? `Reading ${listSentence(readingNames)}. Its claims show here once it's read.` : undefined
      }
      action={
        <div className="flex flex-wrap gap-2">
          <LossRunUploadButton orgId={orgId} />
          {addButton}
        </div>
      }
    >
      {!isLoading && <LossRunDocumentsCard documents={documents} />}
      {isLoading ? (
        <FillSectionCard>
          <SkeletonRows rows={2} />
        </FillSectionCard>
      ) : lossRunRequests.length === 0 ? (
        <FillSectionCard>
          <p className="text-ink-500 text-sm">
            {documents.length > 0
              ? 'No loss run history read from these files yet. Add it by hand if it is missing.'
              : 'No loss runs yet. Upload one, or add loss run history by hand.'}
          </p>
        </FillSectionCard>
      ) : (
        lossRunRequests.map((request) => (
          <FillSectionCard key={request.id}>
            <LossRunClaimsTable orgId={orgId} request={request} />
          </FillSectionCard>
        ))
      )}
      {adding && <AddClaimDialog orgId={orgId} lossRuns={lossRunRequests} onOpenChange={setAdding} />}
    </FillTabSection>
  )
}
